import { useQuery } from '@tanstack/react-query';
import { searchContent, suggestContent, autocompleteProducts } from '@/lib/api/content-search-client';
import type { ContentSearchParams, ContentSuggestParams } from '@/lib/content/solr';

/**
 * React Query hook for searching CMS content (blog, FAQ, pages).
 *
 * Mirrors the pattern used by `useSearchWithFacets` for product search:
 * - Query key includes the full params object for proper cache invalidation
 * - `enabled` option allows deferring the query until URL params are initialized
 * - No staleTime override (defaults to 0, matching product search)
 */
export const useContentSearch = (
  params: ContentSearchParams,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: ['content', 'search', params],
    queryFn: () => searchContent(params),
    enabled: options?.enabled ?? true,
  });
};

/**
 * React Query hook for content title suggestions (autocomplete).
 *
 * @param prefix - The search prefix to autocomplete
 * @param count - Maximum number of suggestions to return (default: 5)
 * @param options - Optional React Query options (e.g., enabled flag)
 */
export const useContentSuggestions = (
  prefix: string,
  count: number = 5,
  options?: { enabled?: boolean },
) => {
  const params: ContentSuggestParams = { prefix, count };

  return useQuery({
    queryKey: ['content', 'suggest', params],
    queryFn: () => suggestContent(params),
    enabled: options?.enabled ?? (prefix.length > 0),
    staleTime: 30_000, // 30s — suggestions change infrequently
  });
};

/**
 * React Query hook for product autocomplete suggestions.
 *
 * @param query - The search query to autocomplete
 * @param count - Maximum number of suggestions to return (default: 5)
 * @param options - Optional React Query options (e.g., enabled flag)
 */
export const useProductSuggestions = (
  query: string,
  count: number = 5,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: ['product', 'autocomplete', query, count],
    queryFn: () => autocompleteProducts(query, count),
    enabled: options?.enabled ?? (query.trim().length >= 2),
    staleTime: 30_000, // 30s — product names change infrequently
  });
};